import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Code2, PaintBucket, Wrench, MapPin } from 'lucide-react';
import PageTransition from '../components/ui/PageTransition';
import HeroSection from '../components/sections/HeroSection';
import SectionHeading from '../components/ui/SectionHeading';
import ServiceCard from '../components/ui/ServiceCard';
import Button from '../components/ui/Button';
import TestimonialCard from '../components/ui/TestimonialCard';
import CtaSection from '../components/sections/CtaSection';
import { services } from '../data/services';
import { testimonials } from '../data/testimonials';

const locations = [
  {
    id: 1,
    city: 'Nottingham',
    country: 'United Kingdom',
    image: 'https://images.pexels.com/photos/19001682/pexels-photo-19001682/free-photo-of-the-exchange-shopping-mall-in-nottingham.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  },
  {
    id: 2,
    city: 'Arizona',
    country: 'United States',
    image: 'https://images.pexels.com/photos/2317408/pexels-photo-2317408.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
  },
  {
    id: 3,
    city: 'Southeast Asia',
    country: 'Multiple Locations',
    image: 'https://images.pexels.com/photos/466685/pexels-photo-466685.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
  }
];

const HomePage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Premium Web Design & Development | Consiliuma Nottingham</title>
        <meta name="description" content="Expert web design and development services in Nottingham. We create beautiful, high-performance websites that drive business growth. Get your free quote today." />
        <meta name="keywords" content="Web Design Nottingham, Web Development, Full Stack Development, British Web Developers" />
        <link rel="canonical" href="/" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Premium Web Design & Development | Consiliuma Nottingham" />
        <meta property="og:description" content="Expert web design and development services in Nottingham. We create beautiful, high-performance websites that drive business growth. Get your free quote today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk" />
        <meta property="og:image" content="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Premium Web Design & Development | Consiliuma Nottingham",
            "description": "Expert web design and development services in Nottingham. We create beautiful, high-performance websites that drive business growth.",
            "url": "https://consiliuma.co.uk",
            "mainEntity": {
              "@type": "WebDesignCompany",
              "name": "Consiliuma",
              "url": "https://consiliuma.co.uk",
              "description": "Expert web design and development services in Nottingham.",
              "areaServed": ["Nottingham", "United Kingdom", "United States", "Southeast Asia"],
              "priceRange": "££",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "hello@consiliuma.co.uk",
                "contactType": "customer service"
              }
            }
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">Premium Web Design & Development Services in Nottingham</h1>

        <HeroSection
          title="Transform Your Online Presence with British Expertise"
          subtitle="Award-winning web design and development services from our Nottingham studio"
          ctaText="Get a Free Quote"
          ctaLink="/quote"
          backgroundImage="https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
        />

        {/* Services Section */}
        <section className="py-16 md:py-24" aria-labelledby="services-heading">
          <div className="container-custom">
            <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Our Services</h2>
            <p className="text-xl text-secondary mb-12 text-center">Comprehensive web solutions tailored to your needs</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {services.map((service) => (
                <ServiceCard
                  key={service.id}
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                />
              ))}
            </div>

            <div className="text-center mt-12">
              <Button to="/services" variant="outline">
                View All Services
              </Button>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="about-heading">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h2 id="about-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6">About Consiliuma</h2>
                <p className="text-xl text-secondary mb-8">British expertise with global reach</p>
                <p className="mb-6">
                  Consiliuma is a premium web development business with British expertise at its core. With offices in Nottingham, UK; Arizona, USA; and Southeast Asia, we deliver exceptional digital experiences to clients around the world.
                </p>
                <p className="mb-6">
                  Our team of full-stack developers specializes in creating modern, high-performance websites and web applications that drive business growth and enhance brand presence online.
                </p>
                <p className="mb-8">
                  From initial concept to ongoing maintenance, we provide comprehensive web solutions that combine technical excellence with creative design and strategic thinking.
                </p>
                <Button to="/about" variant="primary">
                  Learn More About Us
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                {locations.map((location, index) => (
                  <div 
                    key={location.id} 
                    className={`relative rounded-lg overflow-hidden shadow-lg ${index === 0 ? 'col-span-2' : 'col-span-1'}`}
                  >
                    <img 
                      src={location.image} 
                      alt={`${location.city}, ${location.country}`} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                      <div className="flex items-center text-white">
                        <MapPin size={16} className="mr-2" />
                        <div>
                          <p className="font-bold">{location.city}</p>
                          <p className="text-sm">{location.country}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-24" aria-labelledby="testimonials-heading">
          <div className="container-custom">
            <h2 id="testimonials-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">What Our Clients Say</h2>
            <p className="text-xl text-secondary mb-12 text-center">Trusted by businesses around the world</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              {testimonials.slice(0, 2).map(testimonial => (
                <TestimonialCard
                  key={testimonial.id}
                  name={testimonial.name}
                  company={testimonial.company}
                  text={testimonial.text}
                  location={testimonial.location}
                />
              ))}
            </div>

            <div className="text-center mt-12">
              <Button to="/why-choose-us" variant="outline">
                Read More Testimonials
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection
          title="Ready to Transform Your Online Presence?"
          subtitle="Let's discuss how we can help your business grow with a custom web solution."
          buttonText="Get a Free Quote"
          buttonLink="/quote"
        />
      </main>
    </PageTransition>
  );
};

export default HomePage;